import React, { useState, useEffect } from 'react';
import { Paper, Typography, Box, Chip, IconButton, Collapse, Link } from '@mui/material';
import { STATUS_CONFIG, DEFAULT_BANNER, getHighestPriorityStatus } from './BusinessStatusConfig';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

// Define message types and their configurations
const MESSAGE_TYPES = {
  success: {
    icon: CheckCircleIcon,
    color: {
      bg: 'success.light',
      text: 'success.main',
      contrastText: 'success.contrastText'
    }
  },
  info: {
    icon: InfoIcon,
    color: {
      bg: 'info.light',
      text: 'info.main',
      contrastText: 'info.contrastText'
    }
  },
  warning: {
    icon: WarningIcon,
    color: {
      bg: 'warning.light',
      text: 'warning.main',
      contrastText: 'warning.contrastText'
    }
  },
  error: {
    icon: ErrorIcon,
    color: {
      bg: 'error.light',
      text: 'error.main',
      contrastText: 'error.contrastText'
    }
  }
};

// Notification category configurations
const NOTIFICATION_CATEGORIES = {
  promotion: {
    icon: LocalOfferIcon,
    color: {
      bg: '#E8F5E9',
      text: '#2E7D32',
      contrastText: '#1B5E20'
    },
    priority: 600
  },
  status: {
    icon: InfoIcon,
    color: {
      bg: '#E3F2FD',
      text: '#1565C0',
      contrastText: '#0D47A1'
    },
    priority: 500
  },
  announcement: {
    icon: AnnouncementIcon,
    color: {
      bg: '#F3E5F5',
      text: '#6A1B9A',
      contrastText: '#4A148C'
    },
    priority: 400
  },
  alert: {
    icon: WarningIcon,
    color: {
      bg: '#FFE0E0',
      text: '#D32F2F',
      contrastText: '#C62828'
    },
    priority: 800
  },
  general: {
    icon: NotificationsIcon,
    color: {
      bg: '#F5F5F5',
      text: '#424242',
      contrastText: '#212121'
    },
    priority: 300
  }
};

/**
 * BusinessStatusBanner - Displays temporary messages, notifications, or business status banner
 * @param {Object} props - Component props
 * @param {Array} props.tags - Business tags for determining status
 * @param {Object} props.tempMessage - Temporary message object (optional)
 * @param {Object} props.profileData - Business profile data containing customerNotifications
 * @param {boolean} props.isBusinessOwner - Whether current user owns the business
 */
const BusinessStatusBanner = ({ 
  tags = [], 
  tempMessage = null, 
  profileData = null,
  isBusinessOwner = false,
  businessQuality = null,
  // Supplied by BusinessDetails as businessNavigation.handlers
  // .handleEditBusinessDetails, which merges ownerView into the router state.
  // Kept as a callback rather than navigating here so the merge lives in ONE
  // place — the edit form uses location.state as its whole data source, and a
  // second navigate site is a second chance to forget.
  onEditBusiness = null,
}) => {
  const [notifications, setNotifications] = useState([]);
  const [dismissedNotifications, setDismissedNotifications] = useState(new Set());
  const [expandedNotifications, setExpandedNotifications] = useState(new Set());

  // Load notifications from profileData
  useEffect(() => {
    if (profileData && profileData.customerNotifications && Array.isArray(profileData.customerNotifications)) {
      // Filter out expired notifications
      const activeNotifications = profileData.customerNotifications.filter(notification => {
        if (!notification.expiresAt) return true;
        return new Date(notification.expiresAt) > new Date();
      });
      
      // Sort by priority (urgency) and creation date
      activeNotifications.sort((a, b) => {
        const priorityA = NOTIFICATION_CATEGORIES[a.category]?.priority || 300;
        const priorityB = NOTIFICATION_CATEGORIES[b.category]?.priority || 300;
        
        if (priorityA !== priorityB) {
          return priorityB - priorityA; // Higher priority first
        }
        
        return new Date(b.createdAt) - new Date(a.createdAt); // Newer first
      });
      
      setNotifications(activeNotifications);
    }
  }, [profileData]);

  // Handle notification dismissal
  const handleDismissNotification = (notificationId) => {
    setDismissedNotifications(prev => new Set([...prev, notificationId]));
  };

  // Handle notification expansion
  const toggleNotificationExpansion = (notificationId) => {
    setExpandedNotifications(prev => {
      const newSet = new Set(prev);
      if (newSet.has(notificationId)) {
        newSet.delete(notificationId);
      } else {
        newSet.add(notificationId);
      }
      return newSet;
    });
  };

  // Get visible notifications (not dismissed and not expired)
  const visibleNotifications = notifications.filter(notification => 
    !dismissedNotifications.has(notification.id)
  );

  // Check if we should show notifications instead of regular banner
  const hasActiveNotifications = visibleNotifications.length > 0;

  // If we have a temporary message, show it first (highest priority)
  if (tempMessage && tempMessage.type && tempMessage.title) {
    const messageConfig = MESSAGE_TYPES[tempMessage.type] || MESSAGE_TYPES.info;
    const MessageIcon = messageConfig.icon;

    return (
      <Paper
        sx={{
          bgcolor: messageConfig.color.bg,
          color: messageConfig.color.text,
          p: 2,
          mb: 2,
          borderRadius: 2,
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <MessageIcon sx={{ color: messageConfig.color.contrastText }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: messageConfig.color.contrastText }}>
            {tempMessage.title}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ ml: 4 }}>
          {tempMessage.description}
        </Typography>
      </Paper>
    );
  }




  // If we have active notifications, show them
  if (hasActiveNotifications) {
    return (
      <Box sx={{ mb: 2 }}>
        {visibleNotifications.map((notification, index) => {
          const categoryConfig = NOTIFICATION_CATEGORIES[notification.category] || NOTIFICATION_CATEGORIES.general;
          const NotificationIcon = categoryConfig.icon;
          const isExpanded = expandedNotifications.has(notification.id);
          const isLongMessage = notification.message && notification.message.length > 100;
          const displayMessage = isLongMessage && !isExpanded 
            ? `${notification.message.substring(0, 100)}...` 
            : notification.message;

          return (
            <Paper
              key={notification.id || index}
              sx={{
                bgcolor: notification.displayConfig?.backgroundColor || categoryConfig.color.bg,
                color: notification.displayConfig?.textColor || categoryConfig.color.text,
                p: 2,
                mb: index < visibleNotifications.length - 1 ? 1 : 0,
                borderRadius: 2,
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                border: notification.urgency === 'high' ? '2px solid' : '1px solid',
                borderColor: notification.urgency === 'high' 
                  ? categoryConfig.color.contrastText 
                  : 'transparent'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <NotificationIcon sx={{ 
                      color: notification.displayConfig?.textColor || categoryConfig.color.contrastText,
                      fontSize: notification.urgency === 'high' ? '1.2rem' : '1rem'
                    }} />
                    
                    {/* Category and urgency indicators */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <Chip
                        label={notification.category.charAt(0).toUpperCase() + notification.category.slice(1)}
                        size="small"
                        variant="outlined"
                        sx={{ 
                          height: '20px',
                          fontSize: '0.75rem',
                          borderColor: categoryConfig.color.contrastText,
                          color: categoryConfig.color.contrastText
                        }}
                      />
                      
                      {notification.urgency === 'high' && (
                        <Chip
                          label="URGENT"
                          size="small"
                          sx={{ 
                            height: '20px',
                            fontSize: '0.75rem',
                            bgcolor: '#FF5722',
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        />
                      )}
                      
                      {notification.expiresAt && (
                        <Typography variant="caption" sx={{ color: categoryConfig.color.text, opacity: 0.8 }}>
                          Expires: {new Date(notification.expiresAt).toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Typography 
                    variant="body2" 
                    sx={{ 
                      ml: 4,
                      fontWeight: notification.urgency === 'high' ? 600 : 400,
                      lineHeight: 1.4
                    }}
                  >
                    {displayMessage}
                  </Typography>

                  {/* Keywords display */}
                  {notification.keywords && notification.keywords.length > 0 && (
                    <Box sx={{ mt: 1, ml: 4 }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {notification.keywords.slice(0, 3).map((keyword, keyIndex) => (
                          <Chip
                            key={keyIndex}
                            label={keyword}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: '18px',
                              fontSize: '0.6rem',
                              opacity: 0.7,
                              borderColor: categoryConfig.color.text,
                              color: categoryConfig.color.text
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                  {/* Expand/Collapse button for long messages */}
                  {isLongMessage && (
                    <IconButton
                      size="small"
                      onClick={() => toggleNotificationExpansion(notification.id)}
                      sx={{ color: categoryConfig.color.contrastText }}
                    >
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  )}

                  {/* Dismiss button */}
                  <IconButton
                    size="small"
                    onClick={() => handleDismissNotification(notification.id)}
                    sx={{ color: categoryConfig.color.contrastText }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Paper>
          );
        })}
      </Box>
    );
  }

    // ===== Owner-only: business record quality =====
  //
  // Rendered ONLY when isBusinessOwner, from the ownerview endpoint — a
  // customer reading "tell us what services you offer" on a public page would
  // be a poor advertisement for the business.
  //
  // AFTER notifications deliberately. This banner persists until the owner
  // acts, so showing it first would hide time-sensitive customer
  // announcements indefinitely.
  //
  // Derived from the verdict rather than a stored flag: when the owner edits,
  // the assessment re-runs and the tier changes, so the banner clears itself.
  // A stored flag would need clearing, and something would eventually forget.
  const showQualityBanner =
    isBusinessOwner &&
    businessQuality &&
    (businessQuality.tier === 'blocked'
      || businessQuality.categorySuggested
      || businessQuality.fieldsToIgnore?.length) &&
    !businessQuality.ownerConfirmed;

  if (showQualityBanner) {
    const blocked = businessQuality.tier === 'blocked';
    const cfg = blocked ? MESSAGE_TYPES.warning : MESSAGE_TYPES.info;
    const QIcon = cfg.icon;
    const slug = (profileData?.businessName || 'business')
      .replace(/[\s,\.]+/g, '-').toLowerCase();

    return (
      <Paper sx={{ bgcolor: cfg.color.bg, color: cfg.color.text, p: 2, mb: 2,
                   borderRadius: 2, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <QIcon sx={{ color: cfg.color.contrastText }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: cfg.color.contrastText }}>
            {blocked ? 'We need a bit more to write about your business'
                     : 'Confirm a couple of details'}
          </Typography>
          <Chip label="Only you can see this" size="small" variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem', ml: 'auto',
                      borderColor: cfg.color.contrastText, color: cfg.color.contrastText }} />
        </Box>

        {businessQuality.summary && (
          <Typography variant="body2" sx={{ ml: 4 }}>
            {businessQuality.summary}
          </Typography>
        )}

        {businessQuality.categorySuggested && (
          <Typography variant="body2" sx={{ ml: 4, mt: 0.5 }}>
            Your business is listed under <strong>{profileData?.skillName}</strong> — from your
            description it looks more like <strong>{businessQuality.categorySuggested}</strong>.
            {businessQuality.categoryReason ? ` ${businessQuality.categoryReason}` : ''}
          </Typography>
        )}

        {onEditBusiness && (
          <Typography variant="body2" sx={{ ml: 4, mt: 1 }}>
            <Link
              component="button"
              variant="body2"
              onClick={onEditBusiness}
              sx={{ color: 'inherit', textDecoration: 'underline' }}
            >
              Update your business details
            </Link>
          </Typography>
        )}
      </Paper>
    );
  }

  

  // Regular banner logic (no notifications or temp messages)
  const activeStatus = getHighestPriorityStatus(tags);
  const config = activeStatus ? STATUS_CONFIG[activeStatus] : DEFAULT_BANNER;
  const Icon = config.icon;

  return (
    <Paper
      sx={{
        bgcolor: config.color.bg,
        color: config.color.text,
        p: 2,
        mb: 2,
        borderRadius: 2,
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Icon sx={{ color: config.color.contrastText }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: config.color.contrastText }}>
          {config.title}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ ml: 4 }}>
        {config.description}
      </Typography>
    </Paper>
  );
};

export default BusinessStatusBanner;